/**
 * The game shell: owns the systems, the level stack and the frame loop.
 *
 * This file is the integration seam. Subsystems never import each other; they
 * are constructed here, wired through the event bus, and updated in a fixed
 * order every frame:
 *
 *   input -> player -> survival -> entities -> level triggers -> audio -> render -> ui
 *
 * That order matters. The player moves first so everything downstream reacts to
 * where they actually are this frame, and rendering happens last so it draws
 * the settled state rather than a frame-old one.
 */

// One `Vector3`, for reading a peer's drawn chest out of `remoteplayers.ts` so
// the headset's ray can be pointed at it. The only reason this file names three
// at all, and it is never touched on a flat frame.
import * as THREE from 'three';

import { TUNING } from './core/config';
import { makeEventBus } from './core/events';
// `InputSink` is how anything that is not a keyboard feeds the same axes and
// actions the keyboard feeds: the on-screen touch controls do it, and so do the
// XR controllers. See `core/input.ts`.
import { makeInput, type InputManager, type InputSink } from './core/input';
import { clamp01 } from './core/math';
import { makeRNG, randomSeed } from './core/rng';
// Whether this browser has a pointer to lock at all. False on iOS Safari, where
// the API is absent rather than restricted. See `resume`.
import { hasPointerLock } from './core/touch';
import type {
  EntitySystem,
  JournalEntry,
  PlayerController,
  SurvivalSystem,
  UIAction,
  UISystem,
} from './core/systems';
import type {
  AmbienceProfile,
  AudioAPI,
  EntityKind,
  EntityMode,
  ActionName,
  EventBus,
  ExitPoint,
  GameContext,
  GeneratedLevel,
  Interactable,
  InventorySlot,
  ItemKind,
  ItemSpot,
  RenderAPI,
  Vec2,
} from './core/types';

import { makeAudio, voiceOf } from './audio/engine';
import type { VoiceChat } from './audio/voicechat';
import { makeEntitySystem } from './entities/system';
// The shared world. Every one of these is inert without a server: see the
// header of `net/client.ts` for the silence rule this whole feature obeys.
import { makeNetClient, type NetClient, type RemotePeer } from './net/client';
import { makePlayerController } from './player/controller';
import { makeRemotePlayers, type RemotePlayers } from './render/remoteplayers';
import { makeStrikeGesture, type StrikeGesture } from './render/remotestrike';
// `GameRenderer` is `RenderAPI` plus the immersive session the renderer owns.
// `GameContext.render` still takes it as a plain `RenderAPI`.
import { makeRenderer, type GameRenderer } from './render/renderer';
// Interaction from a headset's ray rather than from the camera's forward.
// Every path through it is gated on a live session; see the file header.
import { makeXRInteract, type XRInteract } from './render/xrinteract';
import type { XRIntent, XRIntentSet } from './render/xrintent';
import { decodeFog, encodeFog, SNAPSHOT_VERSION, type RunSnapshot, type SavedSlot } from './save/snapshot';
import { makeSaveSystem, type SaveStore } from './save/storage';
import { makeSurvivalSystem } from './survival/survival';
// The live item table, for checking a snapshot's inventory against what this
// build actually has. A kind that has been retired is skipped rather than
// handed to `itemDef`, which would not find it. `itemDef` itself is the words a
// supply is offered in, which the ray's own prompt needs — see `offerXRTargets`.
import { ITEM_KINDS, itemDef } from './survival/items';
// The player's own voice. Kept with the rest of the game's writing; fired from
// here because this is the only file that sees both the entity bus and the run.
// `NOTES` comes along for the journal restore, which needs the text back.
import { NOTES, RECALL } from './survival/notes';
import type { Veil } from './ui/fade';
import { makeUI, type ScreenDoors } from './ui/ui';
import { generateLevel } from './world/generator';
// The descent is unbounded: `getLevelSpec` wraps past `LEVEL_COUNT` onto deeper,
// harder repeats, so `descend()` never needs to check a limit. `resolveDescent`
// is what turns an exit's `target` into one of those indices.
import { getLevelSpec, pickSlideTarget, resolveDescent } from './world/levels';

const AMBIENCE_FOR: Record<string, AmbienceProfile> = {
  lobby: 'lobby',
  warehouse: 'warehouse',
  pipes: 'pipes',
  poolrooms: 'poolrooms',
  hub: 'lobby',
  // The Funrooms runs the fluorescent bed: the room tone of a function suite is
  // the same hum as the Lobby's, which is most of what is wrong with it.
  funrooms: 'lobby',
  // Level 94 has no bed at all. There is no wind out there, and nothing living
  // in any of the gardens.
  motion: 'silence',
};

/**
 * The noise a slide makes: seconds between one-shots, and the range past which
 * it is not worth starting.
 *
 * A slide is the one exit the player is meant to find, and sound is the only
 * signposting that works through a wall. Something shifts inside the chute
 * every few seconds — it is a positional world sound rather than a cue, so it
 * arrives as a direction to walk in rather than as an instruction.
 */
const SLIDE_SOUND_INTERVAL: readonly [number, number] = [5.5, 9.5];
const SLIDE_SOUND_RANGE = 40;

/**
 * How each exit kind reads under the crosshair. `ExitPoint.kind` is chosen with
 * care by the generator and this is the only thing that ever tells the player
 * about it.
 */
const EXIT_LABEL: Record<ExitPoint['kind'], string> = {
  noclip: 'the gap in the wall',
  stairwell: 'the stairwell',
  hole: 'the hole in the floor',
  door: 'the door',
  slide: 'the slide',
};

/**
 * Interactable ids for exits. World items are keyed by `ItemSpot.id`, which
 * counts up from zero, so exits start well past any level's supply count.
 */
const EXIT_ID_BASE = 100000;

/**
 * Ids for other people, in the headset's ray picking only.
 *
 * `render/xrinteract.ts` works in numbers and peers are keyed by string, so
 * they are offered under their index in one more disjoint range. Nothing is
 * ever *committed* against one of these — a person is a wind-up, not a press —
 * so the id only has to be stable enough to keep the selection from flickering
 * between two people standing together.
 */
const PEER_ID_BASE = 200000;

/**
 * Seconds between routine writes of the run to storage.
 *
 * This is localStorage and the snapshot is a few hundred bytes, so the cost is
 * a rounding error either way; four seconds is chosen from the other end, as
 * the most progress anybody should be able to lose to a crash. Every moment
 * that is actually worth not losing — a descent, the page being hidden, the tab
 * being closed — writes immediately and does not wait for this.
 */
const AUTOSAVE_INTERVAL = 4;

/**
 * The item kinds this build has, as text, for checking a snapshot's inventory.
 * A kind that has since been retired is skipped rather than handed to
 * `itemDef`, which would return nothing and take the restore down with it.
 */
const ITEM_KIND_SET: ReadonlySet<string> = new Set<string>(ITEM_KINDS);

/**
 * How long into a fresh run the world seed may still be adopted.
 *
 * Two clients only stand in the same building if they generated it from the
 * same number, so joining a shared world means taking its seed. That is free
 * for the first breath of a run — the player is standing on the spawn tile
 * watching the level card — and unacceptable a minute later, when adopting it
 * would mean rebuilding the floor under somebody who has been walking around
 * on it. Past this, a mismatched run simply plays on alone. See `onWelcome`.
 */
const ADOPT_GRACE = 6;

/**
 * The name over this player's head, and where it is kept.
 *
 * Its own key rather than a field on the save blob: it is not a setting, it has
 * no UI, and it must survive a `reset()` of the record — being called something
 * different every time you clear your stats is worse than the alternative. Only
 * ever read by the net client, and only ever seen by other people.
 */
const NAME_KEY = 'backrooms.name.v1';

/** Seconds a damage flash takes to fade. */
const DAMAGE_FLASH_DECAY = 1.6;

/** Damage that fills the flash completely. */
const DAMAGE_FLASH_FULL = 22;

/**
 * A stable name for this browser, invented once.
 *
 * Nobody is asked to pick one. A name only exists so that the person walking
 * towards you in a corridor is *somebody* rather than a shape, and four hex
 * digits is enough for that among the sixteen people a world holds. Any
 * storage failure falls through to a fresh name for the session, which is
 * still better than an empty label.
 */
function playerName(): string {
  const invent = (): string => `WANDERER-${Math.floor(Math.random() * 0x10000).toString(16).toUpperCase().padStart(4, '0')}`;
  try {
    const stored = localStorage.getItem(NAME_KEY);
    if (stored && stored.length <= 24) return stored;
    const fresh = invent();
    localStorage.setItem(NAME_KEY, fresh);
    return fresh;
  } catch {
    return invent();
  }
}

/** Nobody else is here. Shared so the offline path allocates nothing per frame. */
const NO_PEERS: readonly RemotePeer[] = [];

/** Scratch for `RemotePlayers.chestOf`. Only ever written inside a session. */
const _chest = new THREE.Vector3();

/**
 * The descent, phase by phase. Generating a floor is the most expensive thing
 * the game does and it blocks; run inline it is a 100–400 ms freeze that reads
 * as a stall. So the screen goes first, the work happens inside `hold`, and the
 * player gets a beat of dark and silence with the level card on it before the
 * new floor arrives. Same cost, and it now reads as having gone somewhere.
 */
type DescentPhase =
  /** Fading out. Nothing has been generated yet; the old floor is still loaded. */
  | 'out'
  /** Black. The generation lands at the top of this phase. */
  | 'hold'
  /** Fading back in over the new floor. */
  | 'in';

interface Descent {
  phase: DescentPhase;
  /**
   * `performance.now()` at which the current phase began, or -1 for "not yet".
   *
   * The fade-out's clock deliberately does not start in `beginDescent`. A CSS
   * transition begins at the first style recalc after the property is written,
   * which is the *next* frame, so a clock started at the write runs a frame
   * ahead of the ramp it is supposed to be waiting for — and the one thing this
   * whole sequence exists to guarantee is that the screen is black before the
   * generator takes the thread. Measured headless, where frames are 200 ms
   * apart, that frame of skew let the work start at 80% opacity.
   */
  since: number;
  /** Level index being descended to. */
  to: number;
}

export class Game {
  readonly events: EventBus = makeEventBus();
  /**
   * The keyboard and mouse, and the sink every other source feeds. Typed wide
   * enough to hand to the XR controllers; `ctx.input` still publishes the plain
   * `InputAPI`, so nothing downstream can tell one source from another.
   */
  private readonly input: InputManager & InputSink;
  private readonly render: GameRenderer;
  private readonly audio: AudioAPI;
  private readonly player: PlayerController;
  private readonly survival: SurvivalSystem;
  private readonly entities: EntitySystem;
  private readonly ui: UISystem & Veil & ScreenDoors;
  private readonly save: SaveStore;

  /* -- The shared world. All four are inert with no server; see `updateNet`. -- */
  private readonly net: NetClient;
  private readonly remotes: RemotePlayers;
  private readonly strike: StrikeGesture;
  /** Null on a browser with no WebRTC, and on any build without the audio engine. */
  private readonly voice: VoiceChat | null;
  private readonly netOff: () => void;

  private level!: GeneratedLevel;
  private levelIndex = 0;
  /** Spec ids this run has visited, so a slide can prefer somewhere new. */
  private readonly visited = new Set<string>();
  private seed = randomSeed();
  private rng = makeRNG(this.seed);
  /**
   * Which level a way down leads to. Its own stream, deliberately: drawing from
   * `rng` at descent time would shift every later `fork(index)` and make the
   * buildings themselves depend on which exits the player happened to take.
   */
  private routes = makeRNG(this.seed ^ 0x52045);

  private time = 0;
  private frame = 0;
  private lastNow = 0;
  private running = false;
  private started = false;
  private dead = false;
  /** The pause *menu*, as distinct from `ctx.paused` ("the simulation is halted"). */
  private paused = false;
  /** True while an immersive session is running but nobody is wearing it. */
  private xrBlurred = false;
  /** Whether the pause on the flat screen is one `checkHeadset` put there. */
  private xrAutoPaused = false;

  /* -- Interaction from a headset's ray. All three are inert without one. -- */

  /** Ray picking, the selection mark and the commit. See `xrinteract.ts`. */
  private readonly xri: XRInteract;
  /**
   * What the session says the player is pointing at, or null.
   *
   * Null on every flat frame, and also inside a session whose presenter does
   * not produce intents — in which case every path below is exactly what it was
   * before this existed, and interaction stays on the camera-forward path.
   */
  private xrRay: XRIntentSet | null = null;
  /** Current red flash, decayed here so the renderer stays a pure sink. */
  private damageFlash = 0;
  /** Guards against a second descend being triggered while one is in flight. */
  private descending = false;
  /** The descent in flight, or null. Non-null halts the simulation. */
  private descent: Descent | null = null;
  /**
   * The bed the current floor is playing. Held so a descent can drop it to
   * nothing and bring the same one back, which is a level going quiet rather
   * than a cut to a different room.
   */
  private ambience: AmbienceProfile = 'menu';
  /**
   * One-shot teaching flags, reset every run.
   *
   * The hound is the only entity that reliably kills, and its counterplay —
   * stop moving, get low — is the opposite of what a cornered player does. It
   * is also entirely undiscoverable from dying to one. So the first hound of a
   * run says the rule out loud once, and if the player acts on it, says that it
   * worked. Wired here rather than in the behaviour because "once per run" is
   * run state, and the behaviour has no idea what a run is.
   */
  private toldAboutHound = false;
  private toldHoundLost = false;
  /** A hound is mid-wind-up and has not committed. See `teachHound`. */
  private houndWoundUp = false;
  /**
   * The exit the player just used. Set from the interact callback, acted on in
   * `checkLevelTriggers` — rebuilding the level from inside `survival.update`
   * would pull the ground out from under the rest of that system's frame.
   */
  private pendingExit: ExitPoint | null = null;
  /** Unregister functions for the current level's exit interactables. */
  private readonly exitHandles: Array<() => void> = [];
  /** Seconds until the next slide one-shot. See `SLIDE_SOUND_INTERVAL`. */
  private slideSoundIn = 0;
  /**
   * Its own stream, so the interval between two of those one-shots cannot
   * reorder any draw the director or the entities make.
   */
  private slideRng = makeRNG(0x511d);

  /** Seconds since the run was last written to storage. See `AUTOSAVE_INTERVAL`. */
  private sinceSave = 0;
  /**
   * Supplies this run took off the *current* floor before the last resume.
   *
   * `loadLevel` takes them out of `level.items` on the way in, which is the only
   * moment anybody can: the survival system copies that array and owns its copy
   * from then on. Held here as well because the pruned array can no longer
   * answer what was pruned, and the next snapshot has to say so.
   */
  private readonly takenOnFloor = new Set<number>();
  /** Note ids read this run, so the journal comes back with everything else. */
  private readonly notesRead = new Set<string>();
  /**
   * The map the player drew, waiting for the map overlay to have caught up with
   * the floor it belongs to.
   *
   * `ui/map.ts` learns about a new level from `ctx.level` inside its own
   * `update`, which has not run yet at the moment a run is restored — so the
   * sheet it would be handed is still the last floor's size, and `importFog`
   * would rightly refuse it. Held for one frame instead. See `step`.
   */
  private pendingFog: Uint8Array | null = null;

  /**
   * A world seed to rebuild this run on, set by `onWelcome` and acted on at the
   * top of the next frame. Never applied from inside the socket callback: that
   * would regenerate the floor from underneath whatever is holding a reference
   * to it halfway through a frame.
   */
  private adoptSeed: number | null = null;
  /**
   * True while the run's seed is still the game's to choose. False the moment
   * the player types one, because a typed seed is a request for a specific
   * building and joining somebody else's is not what they asked for.
   */
  private seedIsOurs = true;
  /** So the "you are not in their building" line is said once per connection. */
  private toldAboutWorld = false;
  /**
   * Supplies on this floor that the server confirmed are ours, whether or not
   * we had room for them at the time. Without it a claim we could not carry
   * would be re-claimed on the next attempt and come back refused — the server
   * has already given it to us, and a second `take` is answered as a loss.
   */
  private readonly myClaims = new Set<number>();
  /**
   * Whether anybody was drawn last frame. Purely so a game with no server does
   * no per-frame work at all for a feature it is not using.
   */
  private sharedLastFrame = false;
  /**
   * The outgoing position packet, filled in and handed over rather than built.
   *
   * `publish` is called on every simulated frame whether or not there is a
   * socket — it has to be, because the floor has to be tracked while offline —
   * and a fresh object sixty times a second for a feature that is switched off
   * is exactly the kind of cost single-player must not pay. The client copies
   * what it needs and never holds it.
   */
  private readonly wire = { floor: 0, x: 0, z: 0, yaw: 0, eye: 0, light: false, health: 100 };

  /** Reused every frame so the loop allocates nothing. */
  private ctx: GameContext;

  constructor(canvas: HTMLCanvasElement, uiRoot: HTMLElement) {
    this.save = makeSaveSystem();
    const settings = this.save.load().settings;

    // The on-screen controls, built here rather than by `core/input.ts` — that
    // file is beneath `ui/` and may not name a module in it, even behind a
    // dynamic import. This factory is the whole of the arrangement: the input
    // manager decides *when* thumbs are driving the game and asks; this decides
    // *what* to build. On a machine with no touch hardware the manager never
    // stores the closure and never calls it, so the chunk is never fetched.
    //
    // `doors` is the pack and the field notes. They are the only two screens
    // that open mid-run off a bare key rather than a bound action, so they are
    // the only two a device with no keyboard cannot reach through the input
    // sink. `this.ui` is built further down and is resolved on the tap, which
    // is long after the constructor has finished.
    this.input = makeInput(canvas, async (sink) => {
      const m = await import('./ui/touchhud');
      return m.makeTouchHud(sink, {
        togglePack: () => this.ui.togglePack(),
        toggleNotes: () => this.ui.toggleNotes(),
      });
    });
    this.input.setSensitivity(settings.sensitivity);
    this.input.setInvertY(settings.invertY);

    this.render = makeRenderer(canvas, settings.render);
    this.audio = makeAudio();
    this.audio.setMasterVolume(settings.masterVolume);
    this.audio.setMuted(settings.muted);

    this.player = makePlayerController(this.events);
    this.player.setHeadBob?.(settings.render.headBob);
    this.survival = makeSurvivalSystem(this.events);
    this.entities = makeEntitySystem(this.events);
    // `render.xr` is the comfort host: it owns the turn style and the tunnel,
    // persists them under its own key, and the in-world settings screen is the
    // only place they can be edited. Inert on a machine with no headset, where
    // that screen is never drawn.
    this.ui = makeUI(uiRoot, this.events, this.render.xr);
    this.ui.setSettings(settings);

    // The shared world. Constructing all of this is free and connects to
    // nothing; `connect()` below is the only thing that opens a socket, and it
    // fails silently and permanently when there is no server behind the origin,
    // which is the case for every build of this game so far.
    this.net = makeNetClient({ name: playerName() });
    this.remotes = makeRemotePlayers();
    this.remotes.setTorchLights(settings.render.quality === 'low' ? 0 : TUNING.net.torchLights);
    this.render.scene.add(this.remotes.object);
    this.strike = makeStrikeGesture(this.remotes, {
      onStrike: (target, amount) => this.net.strike(target, amount),
      // No listeners of its own: `attack` is a bound action now, so the key
      // lives in `core/input.ts` with every other key and the gesture is driven
      // from `updateNet` like anything else the player does.
      key: null,
      mouse: false,
      windUp: TUNING.net.strikeWindUp,
      cooldown: TUNING.net.strikeCooldown,
      damage: TUNING.net.strikeDamage,
      range: TUNING.net.strikeRange,
      aimRadius: TUNING.net.strikeAim,
    });
    this.voice = voiceOf(this.audio);
    this.attachVoice();
    this.netOff = this.wireNet();

    // The ray's interaction. Constructing it adds nothing to the scene, builds
    // no geometry and attaches no listener; `attach()` on session entry is the
    // first moment any of that exists. See `render/xrinteract.ts`.
    this.xri = makeXRInteract({ scene: this.render.scene });

    // Built once, mutated in place each frame.
    this.ctx = {
      dt: 0,
      time: 0,
      frame: 0,
      level: null as unknown as GeneratedLevel,
      levelIndex: 0,
      player: this.player.state,
      stats: this.survival.stats,
      inventory: this.survival.inventory,
      entities: this.entities.entities,
      events: this.events,
      input: this.input,
      audio: this.audio,
      render: this.render,
      rng: this.rng,
      paused: false,
      dead: false,
    };

    this.ui.onAction((a) => this.handleUIAction(a));
    // Installed once and left in place. Offline it lets every pickup straight
    // through, which is exactly the single-player behaviour; online it is the
    // difference between one almond water and two. See `claimSupply`.
    this.survival.setPickupGate(this.claimSupply);
    this.events.on('player:died', ({ cause }) => this.onDeath(cause));
    // The player controller emits noise; the entity system is the only listener
    // that matters, and it must not import the player to hear it.
    this.events.on('player:noise', ({ pos, loudness }) => this.entities.hearNoise(pos, loudness));
    // Nothing else drives the red flash: the hurt event is the only place that
    // knows how hard the hit was.
    this.events.on('player:hurt', ({ amount }) => {
      this.damageFlash = clamp01(Math.max(this.damageFlash, amount / DAMAGE_FLASH_FULL));
    });
    // The journal is per-run; the save file remembers every note ever read.
    this.events.on('ui:note', ({ id }) => {
      this.notesRead.add(id);
      this.save.markNoteFound(id);
    });
    this.events.on('entity:mode', (e) => this.teachHound(e.kind, e.mode, e.prev));
    // The other once-per-run teaching line, said for the same reason and from
    // the same place. The surge fires this at most once a run itself; all that
    // is left here is refusing to talk over a death or a menu.
    this.events.on('surge:warn', () => {
      if (!this.started || this.dead) return;
      this.events.emit('ui:subtitle', { text: RECALL.slideSurge, ms: 4200 });
    });

    // The headset. Both of these are inert on a machine that has none: the
    // handler is only ever run from an affordance that is not built, and
    // `onChange` never fires. See `render/xr.ts`.
    this.render.xr.setEnterHandler(this.enterXR);
    this.render.xr.onChange((presenting) => this.onXRChange(presenting));
    this.render.xr.setActionClaim(this.claimXRAction);

    window.addEventListener('resize', this.onResize);
    // Both, and deliberately not `beforeunload`: that one is ignored on mobile
    // Safari, increasingly ignored elsewhere, and does not reliably fire on the
    // stray Ctrl+W this whole feature exists for. `pagehide` is the close;
    // `visibilitychange` is the phone being locked or the tab being switched
    // away from, which on mobile is very often the last thing that ever happens
    // to the page.
    window.addEventListener('pagehide', this.onLeaving);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onResize();
    this.ui.show('title');
    this.offerResume();
    // The title screen sits on its own bed until a run starts.
    this.ambience = 'menu';
    this.audio.setAmbience('menu');
    this.loadLevel(0);
    // At boot rather than at START, so that in a world that *does* have a
    // server the seed is already known by the time the player presses ENTER and
    // the run is built in everyone else's building the first time. With no
    // server this is five silent failures spread over half a minute and then
    // nothing, which is indistinguishable from never having tried.
    this.net.connect();
    this.startLoop();
  }

  /* ---------------------------------------------------------------- *
   * Run lifecycle
   * ---------------------------------------------------------------- */

  private handleUIAction(a: UIAction): void {
    switch (a.type) {
      case 'start':
        // Default to the boot seed rather than a fresh one: level 0 has already
        // been built behind the title screen from it, so `loadLevel` has nothing
        // to do and START does not hitch. A seed the player typed is theirs and
        // is never traded for a shared world's.
        this.startRun(a.seed ?? this.seed, a.seed === undefined);
        break;
      case 'continue':
        this.onContinue();
        break;
      case 'resume':
        this.resume();
        break;
      case 'torch':
      case 'map':
        // Queued rather than done here. This runs from inside `ui.update`,
        // which is near the end of the frame and *after* everything that reads
        // the input — so a tap issued now would be cleared by `endFrame` having
        // been seen by nobody. Resume first: both of these are things you do to
        // the world, not to a menu.
        this.resume();
        this.pendingTaps.push(a.type === 'torch' ? 'flashlight' : 'map');
        break;
      case 'restart':
        this.startRun(randomSeed(), true);
        break;
      case 'quit':
        // Leaving mid-run still counts as a run; without this, only deaths ever
        // reach the deepest-level and best-time records.
        if (this.started && !this.dead) {
          this.save.recordRun({ survived: this.time, depth: this.levelIndex, died: false });
        }
        this.started = false;
        this.cancelDescent();
        // Leave the world with the run. Standing motionless in a corridor for
        // the twenty seconds it takes the server to reap a silent socket is not
        // what "quit to title" looks like to the people still in there.
        this.net.disconnect();
        this.pause();
        this.ambience = 'menu';
        this.audio.setAmbience('menu');
        // Walking out is a decision, and the snapshot goes with it. Resume must
        // never drop somebody back into a run they chose to leave.
        this.clearRun();
        this.ui.show('title');
        // After `show`, which focuses ENTER on the way in. With no snapshot left
        // this only takes the offer down, but the ordering is the rule.
        this.offerResume();
        // Re-roll so the next run is a new maze; the title keeps the old one as
        // a backdrop until then.
        this.seed = randomSeed();
        break;
      case 'settings-changed': {
        const s = this.save.updateSettings(a.settings);
        this.input.setSensitivity(s.sensitivity);
        this.input.setInvertY(s.invertY);
        this.audio.setMasterVolume(s.masterVolume);
        this.audio.setMuted(s.muted);
        // `updateSettings` returns a fresh object every time, so the renderer
        // has to be told rather than left holding the one it was built with.
        this.render.setSettings(s.render);
        this.player.setHeadBob?.(s.render.headBob);
        // A remote torch is a real `SpotLight` and therefore a shader
        // recompile; a machine that asked for low quality is the one machine
        // that should not be paying for four of them.
        this.remotes.setTorchLights(s.render.quality === 'low' ? 0 : TUNING.net.torchLights);
        break;
      }
      case 'use-slot':
        this.survival.consumeSlot(a.index);
        break;
    }
  }

  /**
   * Begin a run.
   *
   * `seedIsOurs` says whether the game picked the seed or the player did. Only
   * a seed the game picked may be swapped for a shared world's — see
   * `ADOPT_GRACE` — and if a world is already known, the swap happens here,
   * before the floor is built, rather than as a rebuild a second later.
   */
  private startRun(seed: number, seedIsOurs = false): void {
    // Resume is called first and synchronously: this runs inside the click that
    // pressed START, which is the only moment a browser will start a context.
    void this.audio.resume();

    this.seedIsOurs = seedIsOurs;
    if (seedIsOurs && this.net.worldSeed !== null) seed = this.net.worldSeed;
    this.adoptSeed = null;
    this.myClaims.clear();
    this.pendingFog = null;
    // Each run gets told once where it stands relative to everyone else.
    this.toldAboutWorld = false;
    // A run beginning is the natural moment to check for a server again: the
    // client gives up on an origin that has never answered, and this is what
    // starts it over. Idempotent while already connected.
    this.net.connect();

    this.seed = seed;
    this.rng = makeRNG(seed);
    this.routes = makeRNG(seed ^ 0x52045);
    this.ctx.rng = this.rng;
    this.time = 0;
    this.frame = 0;
    this.dead = false;
    // A new run is a new tour of the building.
    this.visited.clear();
    this.cancelDescent();
    this.damageFlash = 0;
    // A player who died to their first hound has still never been told; the
    // lesson goes back on the shelf for the next run.
    this.toldAboutHound = false;
    this.toldHoundLost = false;
    this.houndWoundUp = false;
    // Both of these outlive a death: without clearing them every run after the
    // first would start dead and frozen.
    this.ctx.dead = false;
    this.player.state.frozen = false;
    this.notesRead.clear();
    this.started = true;

    this.loadLevel(0);
    // Emitted after the level exists so a listener that reads `ctx.level` on
    // game:start finds the level it is about to play.
    this.events.emit('game:start', { seed });
    // Immediately, rather than in `AUTOSAVE_INTERVAL` seconds: until this lands
    // the stored run is the *previous* one, and a player who starts fresh and
    // closes the tab in the first breath would be offered a run they abandoned.
    this.saveRun();
    // After the run exists, so it can be told whose building it is standing in.
    // The `welcome` handler covers a socket that arrives later; this covers the
    // one that was already open.
    this.reconcileWorld();
    this.resume();
  }

  /**
   * The seed a floor is generated from.
   *
   * A pure function of the run seed and the descent index, and it has to be:
   * resuming regenerates one floor out of the middle of a run without replaying
   * the descents that led to it, so a derivation that depended on the path
   * would rebuild a different building under the saved coordinates. It was
   * `this.rng.fork(index)`, which consumed a value from a shared stream on
   * every load and did exactly that. Forking a throwaway stream costs one
   * object per floor change and buys the property outright — the same reasoning
   * as `routes` above, and the same property the multiplayer protocol needs
   * when two clients on one world seed have to be standing in one building.
   */
  private levelSeed(index: number): number {
    return makeRNG(this.seed).fork(index).seed;
  }

  /**
   * Build a floor and hand it to every system.
   *
   * `alreadyTaken` carries the `ItemSpot.id`s a resumed run had already looted
   * off this floor. It is applied to the generated level before anything reads
   * `level.items`, because `survival.setLevel` copies that array once and owns
   * its copy from then on.
   */
  private loadLevel(index: number, alreadyTaken?: ReadonlySet<number>): void {
    this.levelIndex = index;
    const spec = getLevelSpec(index);
    // Claims belong to a floor. The descent never goes back up, so nothing on
    // the floor being left can ever be claimed again.
    this.myClaims.clear();
    // Recorded by spec id rather than by index, so the deeper loops count as
    // the same place: arriving at a second, harder Lobby should not make the
    // Lobby look unvisited to the next slide that could send you back to it.
    this.visited.add(spec.id);
    const seed = this.levelSeed(index);
    this.takenOnFloor.clear();

    // The title screen is already standing in a fully built level 0, generated
    // from the boot seed. When the run starts on that same seed there is
    // nothing to regenerate, retexture or remesh — which is the difference
    // between a smooth START and the most expensive operation in the game
    // running inside the click that pressed it.
    const current: GeneratedLevel | undefined = this.level;
    if (!current || current.seed !== seed || current.spec.id !== spec.id) {
      this.level = generateLevel(spec, seed);
      // Only ever on a floor this call generated. The one that was already
      // standing is the title screen's, and a fresh run has to find it stocked.
      if (alreadyTaken && alreadyTaken.size > 0) {
        for (const id of alreadyTaken) this.takenOnFloor.add(id);
        this.level.items = this.level.items.filter((spot) => !this.takenOnFloor.has(spot.id));
      }
      // Order matters. The renderer tears down the old level's views first, the
      // player is placed before anything reads its position, and the entity
      // system clears its bodies in place so `ctx.entities` stays the same array.
      this.render.buildLevel(this.level);
    }
    this.ctx.level = this.level;
    this.ctx.levelIndex = index;

    this.player.setLevel?.(this.level, index);
    this.survival.setLevel?.(this.level, index);
    this.entities.setLevel?.(this.level, index);
    this.registerExits();
    // Seeded from the floor, so the same level sounds the same way twice, and
    // held off for a beat: the first thing a new floor does should not be a
    // noise from somewhere else.
    this.slideRng = makeRNG(seed ^ 0x511d);
    this.slideSoundIn = SLIDE_SOUND_INTERVAL[1];
    this.ambience = AMBIENCE_FOR[spec.generator] ?? 'lobby';
    // The title screen keeps its own bed until the first run starts. Mid-descent
    // the screen is black and the bed is down; the new one is brought up with
    // the picture in `updateDescent`, so the hold is a real beat of silence.
    if (this.started) this.audio.setAmbience(this.ambience, this.descent ? 0 : 1);

    // Drawn from the settled level, so the first frame of the new floor is not
    // one frame of the old floor's lighting.
    this.render.syncItems(this.survival.worldItems);
    this.render.syncEntities(this.entities.entities);
    this.render.syncCamera(this.player.state, this.player.getEyePosition(), this.player.getRoll());
    // The body was placed rather than walked, so the rig has to be moved under
    // it and turned to the spawn facing. Free on a flat frame.
    this.recentreXR();

    // `descending` is deliberately *not* cleared here. It is now released by
    // `updateDescent` when the fade finishes, not when the floor finishes
    // loading — this call happens halfway through a transition that still owns
    // the guard, and clearing it here would let a second exit fire under the
    // black. `startRun` and `cancelDescent` reset it for the paths that skip
    // the transition entirely.
    this.pendingExit = null;
    // A hit taken on the last metre of the old floor should not bleed red onto
    // the new one for another second and a half.
    this.damageFlash = 0;
    this.events.emit('level:loaded', { spec, index });
  }

  /**
   * Make the exits things the player chooses. They used to fire on proximity,
   * which meant walking within 1.6 m of an unmarked point yanked you off the
   * floor with everything you had not looted still on it. As interactables they
   * get the crosshair prompt, and `ExitPoint.kind` finally reaches the player.
   */
  private registerExits(): void {
    for (const off of this.exitHandles) off();
    this.exitHandles.length = 0;
    for (const exit of this.level.exits) {
      this.exitHandles.push(
        this.survival.register({
          id: EXIT_ID_BASE + exit.id,
          pos: exit.pos,
          radius: TUNING.player.interactRange,
          verb: 'Descend',
          label: EXIT_LABEL[exit.kind],
          // Queued rather than taken: see `pendingExit`. Returning false keeps
          // the interactable registered, which `registerExits` clears anyway.
          onInteract: () => {
            this.pendingExit = exit;
            return false;
          },
          // A mouth in a wall rather than a bottle on the carpet, and reachable
          // by a ray from half the distance a supply is: descending is the only
          // unrecoverable act in a run and it shares its button with
          // locomotion, so a stray pinch at a doorway must not drop a floor.
          aimY: TUNING.render.xr.exitAimY,
          aimRadius: TUNING.render.xr.exitAimRadius,
          aimReach: TUNING.render.xr.exitReach,
        }),
      );
    }
  }

  /* ---------------------------------------------------------------- *
   * Resuming a run
   *
   * A run survives the tab being closed. The snapshot is written every few
   * seconds, whenever the page is hidden, and the instant a descent lands;
   * it is deleted on death and on quitting to the title, so "continue" can
   * never drop somebody back into a run that is already over.
   *
   * The building itself is never stored. It is regenerated from the seed and
   * the descent index, and the saved position is then checked against the
   * floor that comes back — see `landingSpot`.
   * ---------------------------------------------------------------- */

  /** Everything worth not losing, as of this instant. */
  private takeSnapshot(): RunSnapshot {
    const p = this.player.state;

    // What is still lying on this floor, subtracted from what was generated on
    // it. `takenOnFloor` carries anything a previous resume already pruned out
    // of `level.items`, which can no longer answer for itself.
    const taken = new Set<number>(this.takenOnFloor);
    const left = new Set<number>();
    for (const spot of this.survival.worldItems) left.add(spot.id);
    for (const spot of this.level.items) {
      if (!left.has(spot.id)) taken.add(spot.id);
    }

    const inventory: SavedSlot[] = [];
    for (const slot of this.survival.inventory) {
      if (slot.count > 0) inventory.push({ kind: slot.kind, count: slot.count });
    }

    return {
      version: SNAPSHOT_VERSION,
      savedAt: Date.now(),
      seed: this.seed,
      levelIndex: this.levelIndex,
      levelSeed: this.level.seed,
      specId: this.level.spec.id,
      elapsed: this.time,
      x: p.pos.x,
      z: p.pos.z,
      yaw: p.yaw,
      pitch: p.pitch,
      health: this.survival.stats.health,
      sanity: this.survival.stats.sanity,
      flashlightCharge: p.flashlightCharge,
      flashlightOn: p.flashlightOn,
      visited: [...this.visited],
      notesRead: [...this.notesRead],
      takenItems: [...taken],
      inventory,
      // The only part of the building that is stored, because it is the only
      // part the player made. Run-length encoded; see `save/snapshot.ts`.
      fog: encodeFog(this.ui.exportFog() ?? new Uint8Array(0)),
    };
  }

  /** Write the run out. Cheap enough to call on a timer; it is localStorage. */
  private saveRun(): void {
    this.sinceSave = 0;
    // A dead run is not resumable and a level 0 built behind the title screen
    // is not a run at all.
    if (!this.started || this.dead || !this.level) return;
    this.save.run.write(this.takeSnapshot());
  }

  private clearRun(): void {
    this.sinceSave = 0;
    this.save.run.clear();
  }

  /** Put the offer on the title screen, or take it away. */
  private offerResume(): void {
    const snap = this.save.run.current;
    if (!snap) {
      this.ui.setResume(null);
      return;
    }
    const spec = getLevelSpec(snap.levelIndex);
    this.ui.setResume({ place: `${spec.name} — ${spec.subtitle}`, savedAt: snap.savedAt });
  }

  /** CONTINUE was pressed. Runs inside that click, like START. */
  private onContinue(): void {
    const snap = this.save.run.current;
    if (!snap) return;
    try {
      this.continueRun(snap);
    } catch (err) {
      // The snapshot passed validation, so this is the other failure: a build
      // whose generator or level order moved under it. Nothing here is worth a
      // black screen — throw the run away and start the fresh one the player
      // would have got without any of this.
      console.error('[resume] could not restore the run; starting a new one:', err);
      this.clearRun();
      this.ui.setResume(null);
      this.startRun(randomSeed(), true);
    }
  }

  /**
   * Go back to a saved run.
   *
   * Same shape as `startRun`, and for the same reasons: the level exists before
   * anything reads `ctx.level`, and `game:start` is what puts every system back
   * to the beginning of a run — full stats, an empty pack, no flashlight, blank
   * paper. The snapshot is applied on top of that known base rather than on top
   * of whatever the last run happened to leave in memory.
   */
  private continueRun(snap: RunSnapshot): void {
    // A saved run is a specific building, and going back to it is a decision
    // the player just made by name. It is never traded for a shared world's
    // seed; if the two differ, `onWelcome` says so and the run plays on alone.
    this.seedIsOurs = false;
    this.adoptSeed = null;
    this.myClaims.clear();
    this.toldAboutWorld = false;
    this.net.connect();

    this.seed = snap.seed;
    this.rng = makeRNG(snap.seed);
    this.routes = makeRNG(snap.seed ^ 0x52045);
    this.ctx.rng = this.rng;
    this.time = snap.elapsed;
    this.frame = 0;
    this.dead = false;
    this.ctx.dead = false;
    this.player.state.frozen = false;
    this.cancelDescent();
    this.damageFlash = 0;
    // The teaching lines are once *per run*, and this is the same run.
    // Whether they have already been said is not worth a field on disk, so a
    // resumed run may repeat one. Better than swallowing the first hound
    // warning somebody ever gets.
    this.toldAboutHound = false;
    this.toldHoundLost = false;
    this.houndWoundUp = false;

    this.visited.clear();
    for (const id of snap.visited) this.visited.add(id);
    this.notesRead.clear();
    for (const id of snap.notesRead) this.notesRead.add(id);
    this.started = true;

    this.loadLevel(snap.levelIndex, new Set(snap.takenItems));
    this.events.emit('game:start', { seed: snap.seed });
    this.restoreRun(snap);
    // A saved run is never rebuilt on somebody else's seed; if this world is a
    // different building, that is the sentence the player gets about it.
    this.reconcileWorld();
    // Last, which also puts the audio context inside the click that got here:
    // `resume()` starts it, and until it does, `audio.play` is a no-op — so the
    // handful of pickup sounds the inventory restore emits above stay silent.
    this.resume();
  }

  /** Apply the snapshot to systems that have just been reset by `game:start`. */
  private restoreRun(snap: RunSnapshot): void {
    // The floor that came back is only the same building if the seed it was
    // generated from and the spec it used both still match. When they do not, a
    // saved coordinate is a point in a building that no longer exists.
    const sameBuilding = this.level.seed === snap.levelSeed && this.level.spec.id === snap.specId;
    const spot = sameBuilding ? this.landingSpot(snap.x, snap.z) : null;
    if (spot) {
      this.player.teleport(spot, snap.yaw);
      // Not covered by `teleport`, and worth having: coming back to the view
      // you left is most of what makes this feel like the same run.
      this.player.state.pitch = snap.pitch;
    } else {
      this.player.teleport(this.level.spawn, this.level.spawnYaw);
      this.events.emit('ui:toast', {
        text: 'The building is not as you left it. You start from the way in.',
        tone: 'info',
      });
    }

    // Set, not nudged. `adjust` twice against whatever the last run left in
    // memory is arithmetic, and a difference that happens to cross zero on the
    // way down fires a death on the frame the run comes back.
    this.survival.setStats({ health: snap.health, sanity: snap.sanity });

    // Rebuilt as a list and handed over in one go rather than fed through
    // `addItem`, which is the *pickup* path: a sound, a toast and a charge
    // grant per slot, none of which belongs to a run being reconstituted.
    const slots: InventorySlot[] = [];
    let hasLight = false;
    for (const slot of snap.inventory) {
      if (!ITEM_KIND_SET.has(slot.kind)) continue;
      const kind = slot.kind as ItemKind;
      // A note is read where it lies and never carried.
      if (kind === 'note') continue;
      if (kind === 'flashlight') hasLight = true;
      slots.push({ kind, count: slot.count });
    }
    this.survival.restoreInventory(slots);
    // Ownership of a torch lives in `player/flashlight.ts`, which normally
    // learns it from an `item:pickup` this restore deliberately does not emit.
    if (hasLight) this.player.restoreFlashlight(snap.flashlightCharge, snap.flashlightOn);

    // The journal, in one call rather than as replayed `ui:note`s — every one of
    // those opens the screen, so eight of them would bury the player in
    // overlays. A note id this build no longer has is simply dropped.
    const read: JournalEntry[] = [];
    for (const id of snap.notesRead) {
      const note = NOTES.find((n) => n.id === id);
      if (note) read.push({ id: note.id, title: note.title, body: note.body });
    }
    this.ui.restoreJournal(read);

    // The map, held until the overlay has caught up with the floor. See
    // `pendingFog`.
    this.pendingFog = decodeFog(snap.fog, this.level.grid.w * this.level.grid.h);

    // After the teleport above, which `loadLevel`'s own recentre came too early
    // to see: a resumed run lands somewhere other than the spawn.
    this.recentreXR();
  }

  /**
   * The saved position, if it is still somewhere a person can stand.
   *
   * Generation is deterministic, so on the build that wrote the snapshot this
   * always passes. It is here for the build that did not: a changed generator
   * puts the old coordinate inside a wall, or inside a piano, and dropping
   * somebody there is worse than dropping them at the spawn.
   */
  private landingSpot(x: number, z: number): Vec2 | null {
    const grid = this.level.grid;
    const cell = grid.worldToCell(x, z);
    if (!grid.inBounds(cell.cx, cell.cy) || !grid.isWalkable(cell.cx, cell.cy)) return null;
    // The same index the player's own collision uses, so "not inside a prop"
    // means the same thing here as it does one frame later.
    if (this.level.propIndex.blocked(x, z, TUNING.player.radius)) return null;
    return { x, z };
  }

  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') this.saveRun();
  };

  private onLeaving = (): void => {
    this.saveRun();
  };

  /* ---------------------------------------------------------------- *
   * The shared world
   *
   * Other people are an *option* this game has, not a thing it is. With no
   * server — which is every build of it so far, and every build opened from a
   * file, and every player behind a network that blocks WebSockets — none of
   * the code below does anything at all: `net/client.ts` fails silently and
   * permanently, `sharedWorld` stays false, no avatar is created, no light is
   * added to the scene, no per-frame work is done and nothing is ever said to
   * the player about any of it. That is the release gate this whole feature
   * lives under; if any of it leaks into single-player, it is a bug.
   *
   * The one thing that has to be shared for any of it to work is the building.
   * `generateLevel` is deterministic and `levelSeed` is a pure function of the
   * run seed and the descent index, so two clients that agree on one number are
   * standing in one maze — nothing about the geometry is ever transmitted.
   * ---------------------------------------------------------------- */

  /**
   * True only when there is a server *and* we are in everybody else's building.
   *
   * The second half matters as much as the first: a client on a different seed
   * still receives perfectly valid positions, and drawing them would put people
   * inside walls and doorways that only exist for them. Better to be alone.
   */
  private get sharedWorld(): boolean {
    return this.net.online && this.net.worldSeed === this.seed;
  }

  private wireNet(): () => void {
    return this.net.on({
      welcome: () => this.reconcileWorld(),

      // Authoritative, and the only thing that removes a supply somebody else
      // is standing over. `mine` is the server confirming our own claim, which
      // is the only moment we are allowed to actually pocket it.
      taken: ({ floor, item, mine }) => {
        if (floor !== this.levelIndex) return;
        if (mine) {
          this.myClaims.add(item);
          this.survival.claimItem(item);
        } else {
          this.survival.removeWorldItem(item);
        }
      },

      // The server has already checked the range against our attacker's last
      // reported position. Health is ours to apply — see `protocol.ts`.
      struck: ({ amount }) => {
        if (this.started && !this.dead && this.sharedWorld) this.survival.damage(amount, 'player');
      },

      said: ({ from, text }) => {
        this.events.emit('ui:subtitle', { text, speaker: this.net.nameOf(from) || 'SOMEONE', ms: 4200 });
      },

      // A new connection is a new answer to "are we in the same building",
      // so the line below is allowed to be said again for it.
      status: (status) => {
        if (status !== 'online') this.toldAboutWorld = false;
      },
    });
  }

  /**
   * Are we in the same building as the people we can hear?
   *
   * Asked when a world welcomes us, and again whenever a run begins on an
   * already-open socket — a player who types a seed into the title screen of a
   * shared world has just walked into their own private building, and being
   * told that is the difference between "nobody is playing" and "you are not
   * where they are".
   */
  private reconcileWorld(): void {
    const worldSeed = this.net.worldSeed;
    if (worldSeed === null || !this.net.online) return;
    if (worldSeed === this.seed) {
      this.pruneTakenSupplies();
      return;
    }
    // Still ours to move: the player is a few seconds into a run they did not
    // pick the seed for, so rebuild it on the world's number instead. Deferred
    // to the top of the next frame — regenerating a floor from inside a socket
    // callback would pull it out from under the frame that is running.
    if (this.seedIsOurs && this.started && !this.dead && this.levelIndex === 0 && this.time < ADOPT_GRACE) {
      this.adoptSeed = worldSeed;
      return;
    }
    // Not ours to move: a typed seed, or a resumed run, or a run already under
    // way. Say so once and plainly, and then leave them alone — this is the one
    // situation where the player genuinely needs to be told something, because
    // otherwise a friend they can hear about is somewhere they will never meet.
    if (this.toldAboutWorld || !this.started) return;
    this.toldAboutWorld = true;
    this.events.emit('ui:toast', {
      text: 'Others are in a different building. Start a new run to join them.',
      tone: 'info',
    });
  }

  /**
   * Vet a pickup against the server. Installed once and left in place: it does
   * nothing at all offline, which is the single-player path.
   *
   * Returning false leaves the supply on the floor. That is the whole point —
   * the server decides who got it, and the `taken` echo above is what turns a
   * claim into an item in somebody's pack. Without this, two people standing
   * over the last almond water both drink it.
   */
  private claimSupply = (spot: ItemSpot): boolean => {
    if (!this.sharedWorld) return true;
    // Already ours: the server said so, we simply had no room at the time.
    // Asking again would be answered as a loss, and destroy it.
    if (this.myClaims.has(spot.id)) return true;
    if (this.net.isTaken(this.levelIndex, spot.id)) {
      this.survival.removeWorldItem(spot.id);
      return false;
    }
    this.net.take(this.levelIndex, spot.id);
    return false;
  };

  /**
   * Take everything already claimed off this floor. The server replays `taken`
   * on floor arrival, which covers the ordinary case; this covers the other
   * one, where we were already standing on the floor when the socket came up.
   */
  private pruneTakenSupplies(): void {
    if (!this.net.online) return;
    for (const spot of [...this.survival.worldItems]) {
      // Notes are read, not owned: two people can both read the same page.
      if (spot.kind === 'note') continue;
      if (this.net.isTaken(this.levelIndex, spot.id)) this.survival.removeWorldItem(spot.id);
    }
  }

  private attachVoice(): void {
    const net = this.net;
    // Structural, not an import: `audio/voicechat.ts` knows nothing about the
    // net client and never will. Signalling blobs are opaque on both sides.
    this.voice?.attach({
      get self() {
        return net.self;
      },
      send: (to, data) => net.signal(to, data),
      onSignal: (fn) => net.on({ signal: (e) => fn(e.from, e.data) }),
    });
  }

  /** Where we are, into the reused packet. `health` is passed so death can lie. */
  private wireState(health: number): typeof this.wire {
    const p = this.player.state;
    const w = this.wire;
    w.floor = this.levelIndex;
    w.x = p.pos.x;
    w.z = p.pos.z;
    w.yaw = p.yaw;
    w.eye = p.eyeHeight;
    w.light = p.flashlightOn;
    w.health = health;
    return w;
  }

  /**
   * The shared world's whole per-frame cost.
   *
   * Offline this is one comparison and a return: `sharedWorld` is false, nobody
   * was drawn last frame, and `publish` is a floor-tracking assignment on a
   * client that has no socket. Nothing allocates.
   */
  private updateNet(dt: number, simulating: boolean): void {
    if (simulating) {
      // Every frame; the throttle to `STATE_HZ` and the collapse to a 1 Hz
      // keepalive when standing still both live inside the client.
      this.net.publish(this.wireState(this.survival.stats.health));
    }

    const shared = this.sharedWorld;
    // Nobody now and nobody last frame: there is nothing to draw and nothing
    // left over to take away, so a single-player game stops here every frame.
    if (!shared && !this.sharedLastFrame) return;

    const peers = shared ? this.net.peers : NO_PEERS;
    // Not while the screen is black: a descent would glide bodies across the
    // cut, and the floor they belong to is changing underneath them anyway.
    // The flag only clears once the retiring sync has actually happened, so a
    // connection lost mid-descent cannot strand a body on the new floor.
    // `aimCamera()` rather than `camera`: both of these aim, sort and face off
    // the camera's *local* transform, which is its world transform on every
    // flat frame and is an offset inside the play space in a headset. It hands
    // back the identical object on a desktop. See `render/renderer.ts`.
    const aim = this.render.aimCamera();
    if (!this.descent) {
      this.remotes.sync(peers, dt, this.time, aim);
      this.sharedLastFrame = shared;
    }
    this.voice?.setPeers(peers);

    const canAct = simulating && shared && !this.descent;
    // In a session the wind-up is aimed down the ray and held with the same
    // action that takes a supply. It has to be: a Vision Pro has no squeeze to
    // hold and no second button to put this on, so the *target* is what decides
    // whether a held pinch is a punch — see `render/xrinteract.ts`. The
    // controller's squeeze is kept alongside it, aimed off the head as before.
    //
    // Both terms are inert on a flat frame: `aim()` is null and `striking` is
    // false for the whole life of a desktop session, so this is the identical
    // pair of calls it has always been.
    const rayAim = this.xri.aim();
    this.strike.hold(canAct && (this.input.isDown('attack') || this.xri.striking));
    this.strike.update(dt, rayAim ?? aim, canAct);
  }

  /* ---------------------------------------------------------------- *
   * The descent
   * ---------------------------------------------------------------- */

  /**
   * Take the screen away, then do the work behind it.
   *
   * Everything from here until `updateDescent` finishes is uninterruptible: the
   * simulation is halted (see `simulating` in `step`), the player is frozen, the
   * pause key is ignored, and `descending` blocks a second exit. The exits are
   * interactables, so without that a player standing between two of them could
   * queue a second descent inside the same fade.
   */
  private beginDescent(exit: ExitPoint): void {
    if (this.descent || this.descending) return;
    this.descending = true;
    this.pendingExit = null;
    // The way out opening. The audio engine bridges this to the noclip one-shot,
    // so it has to fire while the player can still see where they are standing.
    this.events.emit('level:exitFound', { exit });
    // Frozen as well as unsimulated: nothing held down across the transition
    // should carry velocity out the other side.
    this.player.state.frozen = true;
    // Same profile at zero intensity, so this is the room going quiet under you
    // rather than a cut to a different room. It lands with the picture.
    this.audio.setAmbience(this.ambience, 0);
    this.ui.veil(1, TUNING.ui.descendFadeOut);
    // `since` is claimed by the first `updateDescent`, one frame from now, which
    // is when the ramp it gates on actually starts. See `Descent.since`.
    //
    // Where it goes is the exit's business, not this file's: every ordinary way
    // down carries `target: -1`, which `resolveDescent` reads as "the next one",
    // and a slide carries the index of a named level. It also guards the number
    // — a target that is out of range comes back as the next floor rather than
    // as a level index nothing can generate.
    // A slide re-resolves where it goes at the moment it is used, preferring a
    // level this run has not seen. Baked targets alone let a player who has
    // been to two levels bounce between them for the rest of the run — which is
    // exactly what the first route table did, funnelling everyone into a closed
    // Lobby/Funrooms/Motion cycle that five of the twelve levels sat outside of.
    const to = resolveDescent(
      this.levelIndex,
      pickSlideTarget(this.level.spec, this.visited, this.routes, exit.kind === 'slide')
    );
    this.descent = { phase: 'out', since: -1, to };
  }

  /**
   * Advance the descent. Driven by the wall clock rather than by `ctx.dt`: the
   * simulation is stopped for the whole sequence, and these are presentation
   * timings that have to line up with CSS transitions running in real seconds.
   */
  private updateDescent(now: number): void {
    const d = this.descent;
    if (!d) return;
    if (d.since < 0) {
      d.since = now;
      return;
    }
    const T = TUNING.ui;
    const elapsed = (now - d.since) / 1000;

    switch (d.phase) {
      case 'out': {
        if (elapsed < T.descendFadeOut) return;
        // Black. The expensive part goes here: generating and meshing a floor
        // blocks for 100-400 ms and there is now nothing on screen to be late
        // for. The veil animates on opacity alone precisely so it is genuinely
        // opaque by the time the main thread stops answering.
        this.descend(d.to);
        d.phase = 'hold';
        // Restarted *after* the work, so the beat of dark is a beat of dark
        // rather than however much of one the generator left over.
        d.since = performance.now();
        return;
      }
      case 'hold': {
        if (elapsed < T.descendHold) return;
        // The room comes back a moment before the picture does.
        this.audio.setAmbience(this.ambience, 1);
        this.ui.veil(0, T.descendFadeIn);
        d.phase = 'in';
        d.since = now;
        return;
      }
      default: {
        if (elapsed < T.descendFadeIn) return;
        this.descent = null;
        this.descending = false;
        // A death cannot land mid-descent, but unfreezing a corpse would.
        if (!this.dead) this.player.state.frozen = false;
      }
    }
  }

  /** The floor change itself. Only ever called from `updateDescent`, at black. */
  private descend(to: number): void {
    this.events.emit('level:descend', { from: this.levelIndex, to });
    this.loadLevel(to);
    // The moment worth not losing. Arriving on a new floor is the one thing in
    // a run that cannot be done again, so it is written before the screen even
    // comes back rather than up to `AUTOSAVE_INTERVAL` seconds later.
    this.saveRun();
  }

  /** Abandon a transition in flight and give the screen back immediately. */
  private cancelDescent(): void {
    this.descent = null;
    this.descending = false;
    this.ui.veil(0, 0);
  }

  /**
   * The first hound of a run, once. `entity:mode` is the only place the wind-up
   * is visible from outside the behaviour, and the wind-up is the only moment at
   * which the advice is still worth anything — once it is chasing, standing
   * still no longer saves you, so a line fired then would be a taunt.
   */
  private teachHound(kind: EntityKind, mode: EntityMode, prev: EntityMode): void {
    if (kind !== 'hound' || !this.started || this.dead) return;

    if (mode === 'stalk') {
      this.houndWoundUp = true;
      if (this.toldAboutHound) return;
      this.toldAboutHound = true;
      this.events.emit('ui:subtitle', { text: RECALL.houndWarning, ms: 4200 });
      return;
    }
    // It committed. Whatever the player did, it was not the thing that works,
    // so the second line is withdrawn — congratulating someone mid-mauling is
    // worse than saying nothing.
    if (mode === 'chase' || mode === 'attack') {
      this.houndWoundUp = false;
      return;
    }

    // It wound up and thought better of it. Confirming that is the other half
    // of the lesson: without it, standing still and being lucky look identical
    // and nobody does it a second time. `prev` matters — a hound that has just
    // spawned also enters `wander`, and that is not a near miss.
    const nearMiss =
      (mode === 'investigate' && prev === 'stalk') ||
      (mode === 'wander' && (prev === 'stalk' || prev === 'investigate'));
    if (!nearMiss || !this.houndWoundUp || this.toldHoundLost) return;
    this.houndWoundUp = false;
    this.toldHoundLost = true;
    this.events.emit('ui:subtitle', { text: RECALL.houndLost, ms: 3600 });
  }

  private onDeath(cause: string): void {
    if (this.dead || !this.started) return;
    // Before anything freezes: `PeerState` has no downed flag, so zero health
    // is how a body collapses on everyone else's screen instead of standing
    // there upright for the twenty seconds it takes the server to reap it. The
    // client sends health changes without waiting for the throttle.
    this.net.publish(this.wireState(0));
    this.cancelDescent();
    // The run is over. Anything else and CONTINUE would offer to put the player
    // back a few seconds before they died, over and over.
    this.clearRun();
    this.dead = true;
    this.ctx.dead = true;
    this.ctx.paused = false;
    this.player.state.frozen = true;
    this.save.recordRun({ survived: this.time, depth: this.levelIndex, died: true });
    this.events.emit('game:over', { reason: cause, survived: this.time, depth: this.levelIndex });
    this.input.releaseLock();
    this.ui.show('dead');
  }

  /* ---------------------------------------------------------------- *
   * The headset
   *
   * Four things change when a session starts, and all four are here rather
   * than in `render/xr.ts` because they belong to the run, not to the picture.
   * ---------------------------------------------------------------- */

  /**
   * The player asked to put the headset on.
   *
   * Runs inside the click, which is the only moment a browser will start either
   * an audio context or an XR session — so the two share the gesture, exactly
   * as START already shares one with the audio.
   *
   * A refusal is not an error: `enter` resolves false and the flat game is
   * untouched, still on screen, still playable. Nothing is said about it,
   * because the player is looking at the thing that did not change.
   */
  private enterXR = (): void => {
    void this.audio.resume();
    void this.render.xr.enter();
  };

  /**
   * A session starting or ending.
   *
   * Head bob goes first: the vertical arc is baked into `PlayerState.eyeHeight`
   * by the controller, and in a session that field is the player's real head
   * height. An invented head movement laid over a real one is the mismatch that
   * makes people ill, so it is not a preference in here.
   *
   * Leaving a session pauses the run. Whether the headset came off deliberately
   * or the battery died, what is left is a flat screen nobody was looking at
   * with a hound on it, so the kind thing is to stop and put a menu up.
   */
  private onXRChange(presenting: boolean): void {
    this.xrBlurred = false;
    this.xrAutoPaused = false;
    if (presenting) {
      this.player.setHeadBob?.(false);
      // Interaction moves onto the ray for the length of the session, and the
      // survival system's camera-cone focus stands down for the same length.
      // Both halves matter: without the second one a single pinch would be two
      // interactions, taking the note across the room *and* the granola bar at
      // the player's feet, and the two would fight over the prompt.
      this.xri.attach();
      this.survival.setAiming(true);
      this.recentreXR();
      return;
    }
    // Before anything else: the selection mark comes out of the scene and the
    // prompt it was holding is withdrawn, so the flat game is left exactly as
    // it would be had no session ever run.
    this.xri.detach();
    this.xrRay = null;
    // The crosshair takes selection back, and says what it can see. Both halves
    // are needed: `ui:prompt` is edge-triggered, so a player who takes the
    // headset off while standing over an almond water would otherwise be
    // looking at a prompt the session had cleared, with nothing to say it again
    // until they looked away.
    this.survival.setAiming(false);
    this.survival.repeatPrompt();
    this.player.setHeadBob?.(this.save.load().settings.render.headBob);
    if (this.started && !this.dead && !this.paused) {
      this.pause();
      this.ui.show('paused');
    }
  }

  /**
   * The descent, as a headset sees it.
   *
   * `ui.veil` is a CSS opacity ramp on an element that does not exist inside a
   * session, so the same shape is computed here and handed to the shutter. Down
   * hard and held through the black — the generator takes the thread there and
   * a headset that keeps drawing the old floor while it does is worse than one
   * that has gone dark — then out over the same `descendFadeIn` the flat game
   * uses, so arriving on a new floor reads the same way in both.
   */
  private xrShutter(): number {
    const d = this.descent;
    if (!d) return 0;
    if (d.phase !== 'in' || d.since < 0) return 1;
    return 1 - clamp01((this.lastNow - d.since) / 1000 / TUNING.ui.descendFadeIn);
  }

  /**
   * Put the rig under the player and face it where the run says they are
   * facing. Needed wherever the body is placed rather than walked — a level
   * load, a resumed run, a session that has just started.
   */
  private recentreXR(): void {
    const xr = this.render.xr;
    if (xr.presenting) xr.recentre(this.player.state.pos, this.player.state.yaw);
  }

  /**
   * The headset off the head, or a system menu over the session.
   *
   * Frames keep arriving while a session is `visible-blurred` but the poses
   * stop moving, so without this a player who lifts the headset to talk to
   * somebody comes back to whatever walked up to them in the meantime. Only the
   * pause this puts up is taken away again: a player who was already in a menu
   * is left in it.
   */
  private checkHeadset(): void {
    const blurred = !this.render.xr.visible;
    if (blurred === this.xrBlurred) return;
    this.xrBlurred = blurred;
    if (blurred) {
      this.saveRun();
      if (this.started && !this.dead && !this.paused) {
        this.xrAutoPaused = true;
        this.pause();
      }
      return;
    }
    if (!this.xrAutoPaused) return;
    this.xrAutoPaused = false;
    if (this.started && !this.dead) this.resume();
  }

  /* ---------------------------------------------------------------- *
   * Interaction, when the player is pointing rather than looking
   *
   * On a flat screen the survival system settles this by itself: it finds
   * what is under the crosshair and `E` takes it. In a session the aim comes
   * from a hand — or, on a Vision Pro, from a gaze ray that exists only at the
   * instant of a pinch — and the commit comes from that same pinch. The
   * picking, the mark and the timing live in `render/xrinteract.ts`; what
   * lives here is the only thing that file must never learn, which is what any
   * of the things it picked actually *are*.
   * ---------------------------------------------------------------- */

  /**
   * The ray's frame. Only ever reached inside a session.
   *
   * `canAct` is the game's own "may the player touch anything" — false under a
   * menu, a descent, a death — and a false still runs, because that is what
   * takes an abandoned selection off the screen.
   */
  /**
   * Actions an in-world menu row asked for, spent at the top of the next frame.
   *
   * Only ever non-empty in a headset, and only for the run of one frame. See
   * the `torch` and `map` cases in `handleUIAction`.
   */
  private readonly pendingTaps: ActionName[] = [];

  private updateXRInteract(dt: number, canAct: boolean): void {
    const xri = this.xri;
    // A presenter that produces no intents leaves interaction on the
    // camera-forward path entirely, and this pass must not so much as touch the
    // prompt that path is emitting. Nothing below has run in that case.
    if (!this.xrRay) return;
    if (xri.begin(this.xrRay, canAct)) this.offerXRTargets();
    xri.end(dt, this.render.aimCamera(), this.level ? this.level.grid : null);
    // What the ray settled on, back to the system that owns the prompt. It has
    // stopped looking for a focus of its own — see `setAiming` — so this is the
    // only thing naming anything, and there is nothing left to fight over.
    this.survival.setFocusTo(this.byRay(xri.selected));
    if (xri.fired >= 0) this.takeByRay(xri.fired);
  }

  /**
   * Whether a press belongs to something other than travel.
   *
   * Asked by `render/xr.ts` at the top of every frame, once per hand that is
   * pressing, and answered before anything has been spent. Both headsets have
   * one button and no second gesture to give the menus, so the same pinch is
   * potentially a menu press, a pickup and a teleport hop; something has to
   * decide, and only the thing being pointed at knows.
   *
   * The panels win over the world, and the world over the floor. `claimed` is
   * last frame's answer from the ray — the pick has not run yet this frame —
   * which is exact enough because travel does not begin until a press has been
   * held past `HOLD_ARM_TIME`, roughly twenty frames after this first matters.
   */
  private claimXRAction = (intent: XRIntent): boolean => {
    return this.ui.claimsRay(intent.ray) || this.xri.claimed;
  };

  /** The interactable an id belongs to, or null: peers and misses land here. */
  private byRay(id: number): Interactable | null {
    if (id < 0 || id >= PEER_ID_BASE) return null;
    for (const it of this.survival.interactables) {
      if (it.id === id) return it;
    }
    return null;
  }

  /**
   * Everything in the building the ray is allowed to find.
   *
   * The world half is the survival system's own pool, asked for rather than
   * reassembled: the supplies on this floor and the ways down, each already
   * carrying how high off the floor it sits, how big a target it is and how far
   * a ray may reach it. Those are properties of the *thing*, so they are set
   * where the thing is created and this file never has to know which is which.
   *
   * The people are added here, because they are the half `survival/` has never
   * heard of, and they have to be in the same competition — somebody standing
   * between the player and an almond water shields it, which is the whole point
   * of picking along a ray at all.
   */
  private offerXRTargets(): void {
    const xri = this.xri;
    const xr = TUNING.render.xr;
    for (const it of this.survival.interactables) {
      const t = xri.add();
      t.id = it.id;
      t.kind = 'thing';
      t.x = it.pos.x;
      t.y = it.aimY ?? xr.itemAimY;
      t.z = it.pos.z;
      t.radius = it.aimRadius ?? xr.itemAimRadius;
      t.reach = it.aimReach ?? it.radius;
    }

    // Nobody to point at in a single-player game, which is every build of this
    // one so far: `sharedWorld` is false and the loop is never entered.
    if (!this.sharedWorld) return;
    const peers = this.net.peers;
    for (let i = 0; i < peers.length; i++) {
      const peer = peers[i]!;
      // Somebody already on the floor is not a target, which is the same rule
      // `RemotePlayers.pick` applies — you cannot hit somebody twice.
      if (peer.health <= 0) continue;
      // Their *drawn* chest rather than their last reported position, so the
      // ray agrees with the body the player can see. A frame stale, because
      // the avatars are interpolated further down the frame in `updateNet`;
      // at walking speed that is under two centimetres.
      if (!this.remotes.chestOf(peer.id, _chest)) continue;
      const t = xri.add();
      t.id = PEER_ID_BASE + i;
      t.kind = 'person';
      t.x = _chest.x;
      t.y = _chest.y;
      t.z = _chest.z;
      t.radius = TUNING.net.strikeAim;
      // No further than the server will accept a strike from. Past that a hit
      // is refused, and a wind-up that could never land is worse than none.
      t.reach = TUNING.net.strikeRange;
    }
  }

  /**
   * A commit from the ray, turned back into the thing it was.
   *
   * Straight down the interactable's own `onInteract`, through the survival
   * system, which is the same path `E` takes on a flat screen. That is the
   * whole point of routing it this way: a way down is queued for
   * `checkLevelTriggers` and a supply goes through the pickup gate — offline it
   * says yes at once, online it hands the decision to the server — with no
   * chance of the ray becoming a second, subtly different way to take things.
   * Bypassing the gate would be two people drinking the last almond water.
   */
  private takeByRay(id: number): void {
    const target = this.byRay(id);
    if (target) this.survival.interactWith(target);
  }

  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.ctx.paused = true;
    this.input.releaseLock();
    this.events.emit('game:pause', {});
  }

  resume(): void {
    this.paused = false;
    this.ctx.paused = false;
    this.ui.show('hud');
    // Both of these want to be inside the gesture that got us here.
    void this.audio.resume();
    this.events.emit('game:resume', {});

    // Two devices have no pointer to lock, and on both of them asking for one
    // would fail and bounce the player into a pause menu — in the headset's
    // case, one they cannot even see.
    //
    // Stated as "do not gate on a lock when there is nothing to lock" rather
    // than trusting a `true` from `requestLock()`, which is what a touchscreen
    // hands back: the answer is the same, but the reason is legible here
    // instead of buried in `core/input.ts`. A headset is the case that file
    // cannot see, since the pointer lock exists on the machine and simply has
    // nothing to do with how the player is looking around.
    if (this.render.xr.presenting || !hasPointerLock()) return;

    // The browser can refuse the pointer — notably for about a second after the
    // player pressed Escape themselves, which is the single most common way to
    // reach this line. Resuming anyway put them back in the game unable to look
    // around, with no menu and no explanation. Go back to the menu instead, so
    // there is always something to click; clicking the canvas also re-asks.
    void this.input.requestLock().then((locked) => {
      if (locked || !this.started || this.dead || this.paused) return;
      this.paused = true;
      this.ctx.paused = true;
      this.ui.show('paused');
      this.events.emit('game:pause', {});
    });
  }

  /* ---------------------------------------------------------------- *
   * Frame loop
   * ---------------------------------------------------------------- */

  /**
   * The frame loop, driven by three rather than by `requestAnimationFrame`.
   *
   * This is the one change in this file that could plausibly hurt the desktop
   * game, so it is worth being precise about what does and does not differ.
   * `WebGLRenderer.setAnimationLoop` runs a `WebGLAnimation` whose context is
   * `self`, which is `requestAnimationFrame` — the same clock, the same
   * timestamp, the same pacing. `now` is the rAF timestamp either way, so `dt`
   * is computed from identical numbers. The only reason to move at all is that
   * inside an immersive session the window's rAF stops firing and the session's
   * own frame callback takes over; three swaps the two for us on `sessionstart`
   * and `sessionend`, and a game still driving its own `requestAnimationFrame`
   * would simply stop.
   *
   * Two differences are real and both are handled here:
   *
   *  - three requests the next frame *after* the callback returns, where this
   *    file used to request it first. A throw in `step` therefore used to leave
   *    the loop running and now would kill it outright — a live game that spams
   *    the console is bad, one that freezes is worse — so the step is guarded
   *    and the loop carries on exactly as it did.
   *  - the callback is handed an `XRFrame` in a session. Nothing here wants it:
   *    three has already read this frame's viewer pose onto the XR camera by
   *    the time it calls us, which is what `render/xr.ts` reads instead.
   */
  private startLoop(): void {
    if (this.running) return;
    this.running = true;
    this.lastNow = performance.now();
    this.render.renderer.setAnimationLoop((now: number) => {
      const dt = Math.min((now - this.lastNow) / 1000, TUNING.maxDeltaTime);
      this.lastNow = now;
      try {
        this.step(dt);
      } catch (err) {
        // Restores what `requestAnimationFrame` used to give for free: one bad
        // frame is not the end of the run. Logged rather than swallowed.
        console.error('[frame] step threw:', err);
      }
    });
  }

  private step(dt: number): void {
    const ctx = this.ctx;
    const player = this.player.state;
    const xr = this.render.xr;

    // Before `beginFrame`, so the controllers' axes and buttons are latched
    // into the input manager for this frame and the head's yaw is the one the
    // player controller steers by. Nothing runs on a flat frame: `presenting`
    // is false for the entire life of a desktop session.
    if (xr.presenting) {
      xr.beginFrame(dt, this.input, player);
      this.checkHeadset();
      // The descent's veil is a DOM element and DOM does not render in a
      // session, so the shutter stands in for it. Without this a headset gets
      // the generation freeze in full light with nothing to explain it.
      xr.setShutter(this.xrShutter());
      // Whatever this session can say about where the player is pointing, read
      // once and held for the frame. Null before the session's first frame has
      // produced anything, which leaves every path below it exactly as it was.
      this.xrRay = xr.intents;
    }

    this.input.beginFrame();
    // Anything an in-world menu row asked for last frame, now that there is a
    // frame in front of it for the controller and the map to read it in.
    // Empty on every flat frame this game will ever run.
    for (let i = 0; i < this.pendingTaps.length; i++) this.input.tap(this.pendingTaps[i]!);
    this.pendingTaps.length = 0;

    // Before anything reads the level: this rebuilds the run in the shared
    // world's building. Deferred here from the socket callback that decided it.
    if (this.adoptSeed !== null) {
      const seed = this.adoptSeed;
      this.adoptSeed = null;
      this.startRun(seed, false);
    }

    // First, because the phase that generates the next floor wants to land
    // before anything renders it. `lastNow` is this frame's timestamp.
    this.updateDescent(this.lastNow);

    // Escape always toggles the pause menu once a run is under way. `paused` is
    // the menu; `ctx.paused` is the broader "the simulation is halted", which
    // the inventory and the journal also cause. A descent owns the screen
    // outright: a pause menu opened over the black would resume into a
    // transition that had been running underneath it the whole time.
    if (this.started && !this.dead && !this.descent && this.input.pressed('pause')) {
      if (this.paused) this.resume();
      else {
        this.pause();
        this.ui.show('paused');
      }
    }

    // A blocking screen halts the simulation exactly as a pause does, so the
    // UI's own flag is part of the condition rather than a second concept.
    const simulating = this.started && !this.paused && !this.dead && !this.descent && !this.ui.blocking;
    // Published so audio and the renderer can tell a live frame from a held one.
    ctx.paused = !simulating && !this.dead;

    if (simulating) {
      this.time += dt;
      this.frame++;
      ctx.dt = dt;
      ctx.time = this.time;
      ctx.frame = this.frame;

      if (xr.presenting) {
        // The rig has to travel with the body, so the head goes where the stick
        // sent it — and then the *head* has to move the body, so that leaning
        // round a doorframe or stepping behind a desk is a real thing to do.
        // Both halves need to know what the controller alone moved, which is
        // only knowable either side of this call.
        const fromX = player.pos.x;
        const fromZ = player.pos.z;
        this.player.update(ctx);
        const walked = xr.afterPlayer(player, this.level, player.pos.x - fromX, player.pos.z - fromZ, !player.frozen);
        // The player's own legs, and their teleport hops. `PlayerState.speed` is
        // the controller's travel and is zero through both, so the hide latch
        // has to be told or it holds while somebody duck-walks across the room
        // — or dashes across the building. The metres go with it, which is what
        // gives either kind of travel footfalls and a noise entities can hear.
        if (walked > 0) this.player.externalMotion?.(walked / dt, walked);
      } else {
        this.player.update(ctx);
      }
      this.survival.update(ctx);
      // After the survival system, whose camera-cone focus has stood down for
      // the duration of the session (`setAiming`) and left the prompt to the
      // ray; and before the entities, so a way down chosen by pointing at it is
      // queued in time for `checkLevelTriggers` below.
      if (xr.presenting) this.updateXRInteract(dt, true);
      this.entities.update(ctx);
      this.updateSlideSound(dt);
      this.checkLevelTriggers();

      // Last in the frame, so what is written is the state everything else has
      // finished settling. Only while the simulation is live: a run held under
      // a pause menu is not changing, and rewriting it every four seconds of
      // somebody reading their map would be writing the same bytes back.
      this.sinceSave += dt;
      if (this.sinceSave >= AUTOSAVE_INTERVAL) this.saveRun();
    } else {
      ctx.dt = 0;
      // The head is still where the head is with a menu up or a descent in
      // flight: the eye height stays honest and the rig stays where it was.
      if (xr.presenting) {
        xr.afterPlayer(player, this.level, 0, 0, false);
        // Nothing is selectable under a menu or through a descent, and a mark
        // left burning on an almond water while the pause screen is up would be
        // the one thing in the room still asking to be pressed.
        this.updateXRInteract(dt, false);
      }
    }

    // After the simulation and before anything is drawn, so the avatars are
    // interpolated from this frame's state rather than last frame's. Costs
    // nothing at all when there is no server. See `updateNet`.
    this.updateNet(dt, simulating);

    // Audio, render and UI keep running while paused so menus stay alive.
    this.audio.setListener(player.pos, player.yaw);
    // Nothing is chasing you through a fade to black. Held at zero so the
    // adaptive layer drops with the bed instead of playing the old floor's
    // chase music over the new floor's title card.
    this.audio.setTension(this.descent ? 0 : clamp01(this.entities.threat));
    this.audio.update(dt, ctx);

    // The flash decays on wall-clock time so it still fades out on the death
    // screen, where the simulation has stopped.
    if (this.damageFlash > 0) {
      this.damageFlash = Math.max(0, this.damageFlash - dt / DAMAGE_FLASH_DECAY);
    }

    // getEyePosition() and getRoll() carry the lateral sway and the strafe lean,
    // neither of which exists anywhere in PlayerState.
    this.render.syncCamera(player, this.player.getEyePosition(), this.player.getRoll());
    this.render.syncEntities(this.entities.entities);
    this.render.syncItems(this.survival.worldItems);
    this.render.setSanityEffect(1 - clamp01(this.survival.stats.sanity / 100));
    this.render.setDamageFlash(this.damageFlash);
    this.render.setFlashlight(player.flashlightOn, player.flashlightCharge);
    this.render.update(dt, ctx);
    this.render.render();

    this.ui.update(ctx);
    // After the UI, which is where the map first hears about the floor it is
    // drawing. Handing it the sheet before that would be handing it one sized
    // for the last level, which it would rightly refuse. See `pendingFog`.
    if (this.pendingFog) {
      this.ui.importFog(this.pendingFog);
      this.pendingFog = null;
    }
    this.input.endFrame();
  }

  /**
   * Something moving in the chute, every few seconds, from wherever the slide
   * is.
   *
   * This is the only part of a slide's signposting that works through a wall.
   * Lights and arrows need line of sight and a player already looking the right
   * way; a positional one-shot at forty metres is a direction, and a player who
   * walks toward it finds the way down without ever having been told there was
   * one. Fired from here because this is the only file that sees both the level
   * and the audio engine, and only while the simulation is live — a noise
   * arriving under the pause menu belongs to nothing.
   */
  private updateSlideSound(dt: number): void {
    this.slideSoundIn -= dt;
    if (this.slideSoundIn > 0) return;
    this.slideSoundIn = this.slideRng.range(SLIDE_SOUND_INTERVAL[0], SLIDE_SOUND_INTERVAL[1]);

    const from = this.player.state.pos;
    let nearest: ExitPoint | null = null;
    let nearestD2 = SLIDE_SOUND_RANGE * SLIDE_SOUND_RANGE;
    for (const exit of this.level.exits) {
      if (exit.kind !== 'slide') continue;
      const dx = exit.pos.x - from.x;
      const dz = exit.pos.z - from.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < nearestD2) {
        nearestD2 = d2;
        nearest = exit;
      }
    }
    if (!nearest) return;
    // Pitched down and quiet: a long plastic thing settling, not an event. The
    // engine's own distance falloff does the rest of the work.
    //
    // Its own sound rather than a borrowed 'distant-scrape': that one is what
    // the director fires for a fakeout, and a player who has learned it means
    // something is moving would hear the way down and walk away from it.
    this.audio.play('slide-settle', {
      pos: nearest.pos,
      gain: this.slideRng.range(0.4, 0.6),
      rate: this.slideRng.range(0.6, 0.72),
    });
  }

  /**
   * Exits are acted on here rather than inside a subsystem so level flow stays
   * in one place, and after every sim system has finished its frame so that
   * rebuilding the level cannot land in the middle of one.
   */
  private checkLevelTriggers(): void {
    const exit = this.pendingExit;
    this.pendingExit = null;
    // The guard holds for the whole transition, not just this frame: the fade
    // takes the simulation down with it, so nothing can queue a second exit,
    // and `beginDescent` refuses one anyway.
    if (!exit || this.descending) return;
    this.beginDescent(exit);
  }

  dispose(): void {
    // The loop belongs to the renderer now, and it is the same call whether it
    // is currently being driven by the window or by a session.
    this.render.renderer.setAnimationLoop(null);
    this.running = false;
    // A dev-server reload is a page that is about to stop existing with a run
    // in it. It costs nothing to keep.
    this.saveRun();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pagehide', this.onLeaving);
    document.removeEventListener('visibilitychange', this.onVisibility);
    // The shared world first: the voice chat holds a microphone and a handful
    // of peer connections, and the socket is what everything else hangs off.
    this.voice?.detach();
    this.netOff();
    this.net.dispose();
    this.strike.dispose();
    // Takes the selection mark out of the scene and gives its geometry back.
    this.xri.dispose();
    this.render.scene.remove(this.remotes.object);
    this.remotes.dispose();
    // Every system that registered a listener or holds a device handle gets torn
    // down. Missing any one of these stacks a second copy on a dev-server reload.
    this.player.dispose?.();
    this.survival.dispose?.();
    this.entities.dispose?.();
    this.ui.dispose?.();
    this.input.dispose();
    this.audio.dispose();
    this.render.dispose();
    this.events.clear();
  }

  private onResize = (): void => {
    this.render.resize(window.innerWidth, window.innerHeight);
  };
}
